import { useMemo } from "react";
import { useGetTransactionNumberChart } from "../services/AnalyticsPageService";
import storage from "services/storage";
import dayjs from "dayjs";
import { uniq } from "lodash";
import { LineChart } from "components";
import { useLocation } from "react-router-dom";
import { paramParsify } from "utils/queryParams";
import { formatDate } from "utils/formatDate";

const TransactionNumberSection = () => {
  const companyIdLocalStorage = storage.getCompanyID();
  const location = useLocation();
  const queryParams = paramParsify(location?.search);

  const { response: buyResp } = useGetTransactionNumberChart({
    id: companyIdLocalStorage,
    transaction_type: "buy",
  });
  const { response: sellResp } = useGetTransactionNumberChart({
    id: companyIdLocalStorage,
    transaction_type: "sell",
  });

  const datePeriod = useMemo(() => {
    const startPeriod = formatDate.toFullDate(
      queryParams?.period_start || Date.now(),
    );
    const endPeriod = formatDate.toFullDate(
      queryParams?.period_end || Date.now(),
    );

    return (
      <p className='text-[#5F737F] text-sm'>{`${startPeriod} - ${endPeriod}`}</p>
    );
  }, [queryParams]);

  const chartData = useMemo(() => {
    let labels = [];
    const buyData = [];
    const sellData = [];

    // listing labels from buy & sell
    buyResp?.data?.forEach((el) => {
      labels?.push(dayjs(el?.period)?.format("YYYY-MM-DD"));
    });
    sellResp?.data?.forEach((el) => {
      labels?.push(dayjs(el?.period)?.format("YYYY-MM-DD"));
    });

    // modifiy labels
    labels = uniq(labels)
      ?.sort((a, b) => -b)
      ?.map((el) => dayjs(el)?.format("DD MMM"));

    // insert buy & sell data based on modified labels
    labels?.forEach((label) => {
      const foundBuyData =
        buyResp?.data?.find(
          (el) => dayjs(el?.period)?.format("DD MMM") === label,
        )?.qty ?? 0;
      const foundSellData =
        sellResp?.data?.find(
          (el) => dayjs(el?.period)?.format("DD MMM") === label,
        )?.qty ?? 0;

      buyData.push(foundBuyData);
      sellData.push(foundSellData);
    });

    // create datasets based on generated buy, sell, and modified label
    const datasets = [
      {
        label: "Buy",
        borderColor: "#039E5A",
        pointBackgroundColor: "#039E5A",
        data: buyData,
      },
      {
        label: "Sell",
        borderColor: "#E52A49",
        pointBackgroundColor: "#E52A49",
        data: sellData,
      },
    ];

    return {
      labels,
      datasets,
    };
  }, [buyResp, sellResp]);

  return (
    <div className='section h-full'>
      <p className='font-bold text-lg mb-6'>Number of Transactions</p>
      <div className='flex flex-row justify-between mb-4'>
        {datePeriod}
        <div className='flex flex-row gap-4'>
          <div className='flex flex-row gap-2 items-center'>
            <div className='rounded-full w-1 h-1 p-1 bg-[#039E5A]' />
            <p className='text-xs'>Buy</p>
          </div>
          <div className='flex flex-row gap-2 items-center'>
            <div className='rounded-full w-1 h-1 p-1 bg-[#E52A49]' />
            <p className='text-xs'>Sell</p>
          </div>
        </div>
      </div>
      <p className='text-[#5F737F] text-[9px] ml-5 mb-3'>No. of transactions</p>
      <LineChart id='transaction-number-chart' data={chartData} />
    </div>
  );
};

export default TransactionNumberSection;
